import {
  useEffect, useLayoutEffect, useRef, useState, useCallback, memo, PropsWithChildren,
} from 'react';
import { createUseStyles } from 'react-jss';
import cn from 'classnames';

import LoaderIndicator from 'app/components/common/LoaderIndicator';
import Easing from 'app/lib/Easing';
import { useSelector } from 'app/store/utils/redux/hooks';

const LoaderAnimation = (props: PropsWithChildren) => {
  const {
    children,
  } = props;
  const classes = useStyles();

  const cardProgress = useSelector((state) => state?.card?.meta?.progress || 0);
  const cardStatus = useSelector((state) => state?.card?.meta?.status || 'init');
  const [isShowLoadingText, setShowLoadingText] = useState(false);
  const [isShowLoader, setShowLoader] = useState(true);

  const showLoadingTextRef = useRef<any>();
  const removeLoaderRef = useRef<any>();
  const containerRef = useRef<any>();
  const percentRef = useRef<any>();
  const easeRef = useRef<any>();

  const isReady = cardStatus === 'ready';

  const handleProgress = useCallback((value: number) => {
    if (!percentRef.current && easeRef.current) {
      easeRef.current.break();
      easeRef.current = null;
    }
    if (!percentRef.current) {
      return;
    }
    (percentRef.current as HTMLSpanElement).textContent = value.toFixed(0);
  }, []);

  useEffect(() => {
    showLoadingTextRef.current = setTimeout(() => {
      setShowLoadingText(true);
    }, 1000);

    return () => {
      if (showLoadingTextRef.current) {
        clearTimeout(showLoadingTextRef.current);
      }
      if (removeLoaderRef.current) {
        clearTimeout(removeLoaderRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isReady && !showLoadingTextRef.current) {
      showLoadingTextRef.current = setTimeout(() => {
        setShowLoadingText(true);
      }, 1000);
    }
    if (isReady && isShowLoader) {
      removeLoaderRef.current = setTimeout(() => {
        setShowLoader(false);
      }, 300);
    }
  }, [isReady, isShowLoader, setShowLoader]);

  useLayoutEffect(() => {
    if (!percentRef.current) {
      return () => {};
    }
    const startValue = Number((percentRef.current as HTMLSpanElement).textContent);
    const endValue = cardProgress * 100;
    if (startValue === endValue) {
      return () => {};
    }
    const ease = new Easing(Easing.fn.easeLinear, startValue, endValue, 500, handleProgress);
    easeRef.current = ease.start(handleProgress);
    return () => {
      easeRef.current.break();
    };
  }, [cardProgress, handleProgress]);

  const handleCloseFrame = useCallback((event: any) => {
    // eslint-disable-next-line no-restricted-globals
    if (window.top === window) {
      return;
    }
    if (!event.target.contains(containerRef.current)) {
      return;
    }
    const message = { sender: 'card-snacks-web-player', type: 'PREVIEW_CLOSED' };
    window.parent.postMessage(message, '*');
  }, []);

  useLayoutEffect(() => {
    document.addEventListener('click', handleCloseFrame);
    return () => document.removeEventListener('click', handleCloseFrame);
  }, [handleCloseFrame]);

  return (
    <div className={classes.container} ref={containerRef}>
      <div className={cn(classes.children, { [classes.loading]: !isReady })}>{children}</div>
      {isShowLoader && (
        <div className={cn(classes.loader, { [classes.show]: !isReady })}>
          <div className={cn(classes.text, { [classes.show]: isShowLoadingText })}>Loading your card...</div>
          <LoaderIndicator>
            <div className={classes.percent}>
              <small className={classes.hidden}>%</small>
              <span ref={percentRef}>0</span>
              <small>%</small>
            </div>
          </LoaderIndicator>
        </div>
      )}
    </div>
  );
};

const useStyles = createUseStyles({
  container: {
    position: 'relative',
    display: 'flex',
    flex: 1,
  },
  children: {
    /* transition: 600ms transform cubic-bezier(0.210, 0.050, 0.380, 1.000), 600ms opacity cubic-bezier(0.210, 0.050, 0.380, 1.000); */
    /* transform-origin: 50% 90%; */
  },
  loading: {
    transition: 'none',
    transform: 'scale(0.15, 0.15)',
    opacity: 0,
  },
  loader: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  text: {
    fontFamily: '"Avenir Next Demi", sans-serif',
    fontSize: '2em',
    lineHeight: 0,
    fontWeight: 'normal',
    color: '#26bda5',
    padding: '11.5rem 0 7.5rem',
    transition: 'opacity .3s ease',
    opacity: 0,
  },
  percent: {
    '&': {
      display: 'flex',
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      justifyContent: 'center',
      alignItems: 'center',
    },
    '& span': {
      fontSize: '1.8rem',
      fontWeight: 'bold',
      color: '#444',
    },
    '& small': {
      fontSize: '1.4rem',
      margin: '0 0.1rem',
      color: '#666',
    },
  },
  hidden: {
    visibility: 'hidden',
  },
  show: {
    '&:first-child': {
      opacity: 1,
    },
  },
});

export default memo(LoaderAnimation);
